import React, { useRef, useState, forwardRef } from 'react';
import styled from 'styled-components';
import { Box } from '../grid/Grid';
import { Tooltip } from '../tooltip/Tooltip';
import { useDefaultRef } from '../utils/useDefaultRef';
import { useEventCallback } from '../utils/useEventCallback';
import { themeGet } from '@styled-system/theme-get';

const Rail = styled.div`
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: ${themeGet('slider.rail.height')};
  border-radius: ${themeGet('slider.rail.borderRadius')};
  background-color: ${themeGet('slider.rail.backgroundColor')};
  transition: background-color 0.3s;
`;

const Thumb = styled.div`
  box-sizing: border-box;
  position: absolute;
  left: ${props => props.left};
  right: auto;
  width: ${themeGet('slider.thumb.width')};
  height: ${themeGet('slider.thumb.height')};
  margin-top: ${themeGet('slider.thumb.marginTop')};
  background-color: ${themeGet('slider.thumb.backgroundColor')};
  border: ${themeGet('slider.thumb.border')};
  border-radius: ${themeGet('slider.thumb.borderRadius')};
  cursor: pointer;
  transform: translate(-50%);

  &:hover {
    box-shadow: ${themeGet('slider.thumb.hover.boxShadow')};
  }
`;

const Track = styled.div`
  position: absolute;
  left: ${props => props.minValue || 0};
  width: ${props => props.maxValue || 0};
  right: auto;
  height: ${themeGet('slider.track.height')};
  background-color: ${themeGet('slider.track.color')};
  border-radius: ${themeGet('slider.track.borderRadius')};
`;

const Wrap = styled(Box).attrs({
  height: '12px',
  py: '4px',
})`
  flex: 1;
  position: relative;
  touch-action: none;
  list-style: none;
  cursor: pointer;

  &:hover {
    ${Rail} {
      background-color: ${themeGet('slider.rail.hover.backgroundColor')};
    }
  }
`;

function ownerDocument(node) {
  return (node && node.ownerDocument) || document;
}

function getDecimalPrecision(num) {
  // This handles the case when num is very small (0.00000001), js will turn this into 1e-8.
  // When num is bigger than 1 or less than -1 it won't get converted to this notation so it's fine.
  if (Math.abs(num) < 1) {
    const parts = num.toExponential().split('e-');
    const matissaDecimalPart = parts[0].split('.')[1];
    return (matissaDecimalPart ? matissaDecimalPart.length : 0) + parseInt(parts[1], 10);
  }

  const decimalPart = num.toString().split('.')[1];
  return decimalPart ? decimalPart.length : 0;
}

function valueToPercent(value, min, max) {
  return ((value - min) / (max - min)) * 100;
}

function percentToValue(percent, min, max) {
  return (max - min) * percent + min;
}

function roundValueToStep(value, step, min) {
  return Number((Math.round((value - min) / step) * step + min).toFixed(getDecimalPrecision(step)));
}

const clamp = (max, min) => value => Math.max(Math.min(max, value), min);

export const Slider = forwardRef(function Slider(
  { min = 0, max = 100, step = 1, value: propValue, onChange, onMouseDown },
  ref
) {
  const isControlled = useRef(propValue != null);
  const [value, setValue] = useState(clamp(max, min)(propValue || 100));
  const mouseDownPosition = useRef();
  const [sliderRef, setSliderRef] = useDefaultRef(ref);
  const [showTooltip, setShowTooltip] = useState(false);

  if (isControlled.current && propValue !== value) {
    setValue(propValue);
  }

  const handleMouseMove = useEventCallback(event => {
    event.stopPropagation();
    const { left, width } = sliderRef.current.getBoundingClientRect();
    const pageX = event.pageX || event.changedTouches[0].pageX;
    const p = (pageX - left) / width;
    let result = clamp(max, min)(percentToValue(p, min, max));
    if (step) {
      result = roundValueToStep(result, step, min);
    }
    if (!isControlled.current) {
      setValue(result);
    }
    if (onChange) {
      onChange(result);
    }
  });

  const handleMouseDown = useEventCallback(event => {
    if (onMouseDown) {
      onMouseDown(event);
    }
    event.preventDefault();
    event.stopPropagation();
    setShowTooltip(true);
    mouseDownPosition.current = event.pageX;
    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('mousemove', handleMouseMove);
    doc.addEventListener('mouseup', handleMouseUp);

    doc.addEventListener('touchmove', handleMouseMove);
    doc.addEventListener('touchend', handleMouseUp);
  });

  const handleMouseUp = useEventCallback(() => {
    setShowTooltip(false);
    const doc = ownerDocument(sliderRef.current);
    doc.removeEventListener('mousemove', handleMouseMove);
    doc.removeEventListener('mouseup', handleMouseUp);

    doc.removeEventListener('touchmove', handleMouseMove);
    doc.removeEventListener('touchend', handleMouseUp);
  });

  const percent = valueToPercent(clamp(max, min)(value), min, max);

  return (
    <Wrap ref={setSliderRef} onMouseDown={handleMouseMove}>
      <Rail></Rail>
      <Track maxValue={`${percent}%`}></Track>
      <Tooltip title={value} open={showTooltip}>
        <Thumb left={`${percent}%`} onMouseDown={handleMouseDown} onTouchStart={handleMouseDown}></Thumb>
      </Tooltip>
    </Wrap>
  );
});

const defaultRangeValue = [0, 100];
export const RangeSlider = forwardRef(function RangeSlider(props, ref) {
  const { min = 0, max = 100, step = 1, value: propValue, onChange } = props;
  const isControlled = useRef(propValue != null);
  const [propLValue, propRValue] = propValue || defaultRangeValue;
  const [rValue, setRValue] = useState(clamp(max, min)(propRValue));
  const [lValue, setLValue] = useState(clamp(max, min)(propLValue));
  if (isControlled.current && propLValue !== lValue) {
    setLValue(propLValue);
  }

  if (isControlled.current && propRValue !== rValue) {
    setRValue(propRValue);
  }

  const mouseDownPosition = useRef();
  const [sliderRef, setSliderRef] = useDefaultRef(ref);
  const [showLTooltip, setShowLTooltip] = useState(false);
  const [showRTooltip, setShowRTooltip] = useState(false);

  const handleRValueChange = useEventCallback(event => {
    const { left, width } = sliderRef.current.getBoundingClientRect();
    const pageX = event.pageX || event.changedTouches[0].pageX;
    const p = (pageX - left) / width;
    let result = clamp(max, lValue)(percentToValue(p, min, max));
    if (step) {
      result = roundValueToStep(result, step, min);
    }
    if (onChange) {
      onChange([lValue, result]);
    }
    if (!isControlled.current) {
      setRValue(result);
    }
  });

  const handleLValueChange = useEventCallback(event => {
    const { left, width } = sliderRef.current.getBoundingClientRect();
    const pageX = event.pageX || event.changedTouches[0].pageX;
    const p = (pageX - left) / width;
    let result = clamp(rValue, min)(percentToValue(p, min, max));
    if (step) {
      result = roundValueToStep(result, step, min);
    }
    if (onChange) {
      onChange([result, rValue]);
    }
    if (!isControlled.current) {
      setLValue(result);
    }
  });

  const handleMinValueMouseDown = useEventCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    setShowLTooltip(true);
    mouseDownPosition.current = event.pageX;
    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('mousemove', handleLValueChange);
    doc.addEventListener('mouseup', handleMouseUp);

    doc.addEventListener('touchmove', handleLValueChange);
    doc.addEventListener('touchend', handleMouseUp);
  });

  const handleMaxValueMouseDown = useEventCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    setShowRTooltip(true);
    mouseDownPosition.current = event.pageX;
    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('mousemove', handleRValueChange);
    doc.addEventListener('mouseup', handleMouseUp);

    doc.addEventListener('touchmove', handleRValueChange);
    doc.addEventListener('touchend', handleMouseUp);
  });

  const handleMouseUp = useEventCallback(() => {
    const doc = ownerDocument(sliderRef.current);
    doc.removeEventListener('mousemove', handleRValueChange);
    doc.removeEventListener('mousemove', handleLValueChange);
    doc.removeEventListener('mouseup', handleMouseUp);

    doc.removeEventListener('touchmove', handleRValueChange);
    doc.removeEventListener('touchmove', handleLValueChange);
    doc.removeEventListener('touchend', handleMouseUp);
    setShowLTooltip(false);
    setShowRTooltip(false);
  });

  const handleClick = useEventCallback(event => {
    const { left, width } = sliderRef.current.getBoundingClientRect();
    const pageX = event.pageX || event.changedTouches[0].pageX;
    const p = (pageX - left) / width;
    let result = clamp(max, min)(percentToValue(p, min, max));
    if (step) {
      result = roundValueToStep(result, step, min);
    }
    if (result <= lValue) {
      if (onChange) {
        onChange([result, rValue]);
      }

      if (isControlled.current) {
        setLValue(result);
      }
    } else {
      if (onChange) {
        onChange([lValue, result]);
      }

      if (isControlled.current) {
        setRValue(result);
      }
    }
  });
  const rPercent = valueToPercent(clamp(max, min)(rValue), min, max);
  const lPercent = valueToPercent(clamp(max, min)(lValue), min, max);
  return (
    <Wrap ref={setSliderRef} onMouseDown={handleClick}>
      <Rail></Rail>
      <Track minValue={`${lPercent}%`} maxValue={`${rPercent - lPercent}%`}></Track>
      <Tooltip title={lValue} open={showLTooltip}>
        <Thumb
          left={`${lPercent}%`}
          onMouseDown={handleMinValueMouseDown}
          onTouchStart={handleMinValueMouseDown}
        ></Thumb>
      </Tooltip>
      <Tooltip title={rValue} open={showRTooltip}>
        <Thumb
          left={`${rPercent}%`}
          onMouseDown={handleMaxValueMouseDown}
          onTouchStart={handleMaxValueMouseDown}
        ></Thumb>
      </Tooltip>
    </Wrap>
  );
});
